import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTablePreferences, ColumnConfig } from '../../../hooks/useTablePreferences';
import useDataTableViews, { UseDataTableViewsReturn } from './useDataTableViews';
import { calculateTableWidth } from '../utils/tableHelpers';
import * as listViewService from '../../../services/listViewService';

export interface UseTablePageOptions<T> {
  entityType: 'patients' | 'users' | 'privileges' | 'smsTemplates' | 'emailTemplates' | 'clinics' | 'meetingTypes' | 'summaryTypes' | 'prompts' | 'activityLogs' | 'questionnaires' | 'questionnaireTypes' | 'patientNotifications' | 'meetings' | 'medications' | 'wellness-configs' | 'wellness-items' | 'wellness-tips' | 'diagnosis-types' | 'tasks' | 'taskRules' | 'internalTasks' | 'internalTaskTypes' | 'referrals' | 'leads' | 'services';
  columns: ColumnConfig[];
  tenantId?: number | null;
  getSortValue: (item: T, sortBy: string) => any;
  getSearchText?: (item: T) => string[];
  filters?: Record<string, any>;
  onFiltersChange?: (filters: Record<string, any>) => void;
}

export interface UseTablePageReturn<T> {
  // Data management
  data: T[];
  setData: (data: T[]) => void;
  sortedData: T[];
  loading: boolean;
  setLoading: (v: boolean) => void;
  error: string | null;
  setError: (v: string | null) => void;
  loadData: (fetcher: () => Promise<T[]>, errorMessage?: string) => Promise<void>;

  // Search
  searchQuery: string;
  setSearchQuery: (q: string) => void;

  // Sort
  sortBy: string | null;
  sortOrder: 'asc' | 'desc';
  setSortBy: (s: string | null) => void;
  setSortOrder: (o: 'asc' | 'desc') => void;

  // Row menu
  menuPosition: { top: number; left: number } | null;
  selectedItem: T | null;
  handleRowClick: (e: React.MouseEvent, item: T) => void;
  handleMenuClose: () => void;

  // Status menu
  statusAnchorEl: HTMLElement | null;
  handleStatusClick: (e: React.MouseEvent<HTMLElement>, item: T) => void;
  handleStatusMenuClose: () => void;

  // Column management
  visibleColumns: string[];
  totalWidth: number;
  tablePrefs: ReturnType<typeof useTablePreferences>;
  manageColumnsOpen: boolean;
  setManageColumnsOpen: (v: boolean) => void;

  // Views
  viewsHook: UseDataTableViewsReturn;

  // Pre-built prop objects for spreading
  toolbarProps: {
    count: number;
    filteredCount: number;
    searchQuery: string;
    onSearchChange: (q: string) => void;
    views: listViewService.ListView[];
    currentViewId: string | null;
    onSelectView: (viewId: string | number) => void;
    onSaveView: () => void;
    onSaveAsView: () => void;
    onDeleteView: (viewId: string) => void;
    onSetDefaultView: (viewId: string) => void;
    onManageColumns: () => void;
  };
  headerProps: {
    columns: ColumnConfig[];
    visibleColumns: string[];
    getColumnWidth: (key: string) => number;
    onColumnResize: (key: string, newWidth: number) => void;
    onSort: (columnKey: string) => void;
    getSortIndicator: (columnKey: string) => string | null;
  };
  containerProps: {
    totalWidth: number;
  };
  menuProps: {
    position: { top: number; left: number } | null;
    open: boolean;
    onClose: () => void;
  };
  manageColumnsProps: {
    open: boolean;
    columns: ColumnConfig[];
    visibleColumns: string[];
    columnOrder: string[];
    onColumnsChange: (visibleColumns: string[]) => void;
    onOrderChange: (columnOrder: string[]) => void;
    onResetDefaults: () => void;
    onClose: () => void;
  };
  saveViewModalProps: {
    open: boolean;
    onSave: (viewName: string, isPublic: boolean) => Promise<void>;
    onClose: () => void;
  };
  tableBodyProps: {
    visibleColumns: string[];
    columns: ColumnConfig[];
    getColumnWidth: (key: string) => number;
    onColumnResize: (key: string, newWidth: number) => void;
    onSort: (columnKey: string) => void;
    getSortIndicator: (columnKey: string) => string | null;
    totalWidth: number;
  };
}

/**
 * Main orchestrator hook for table pages.
 * Combines all common table state and handlers so pages only provide entity-specific code.
 */
function useTablePage<T>({
  entityType,
  columns,
  tenantId,
  getSortValue,
  getSearchText,
  filters = {},
  onFiltersChange,
}: UseTablePageOptions<T>): UseTablePageReturn<T> {
  // Core data state
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Sort — persisted to localStorage per entityType
  const sortStorageKey = `table_sort_${entityType}`;
  const _savedSort = (() => {
    try { return JSON.parse(localStorage.getItem(sortStorageKey) || 'null'); } catch { return null; }
  })();
  const [sortBy, setSortBy] = useState<string | null>(_savedSort?.sortBy ?? null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(_savedSort?.sortOrder ?? 'asc');

  // Persist sort to localStorage whenever it changes
  useEffect(() => {
    try { localStorage.setItem(sortStorageKey, JSON.stringify({ sortBy, sortOrder })); } catch {}
  }, [sortBy, sortOrder, sortStorageKey]);

  // Row menu
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);

  // Status menu
  const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLElement | null>(null);

  // Column management
  const [manageColumnsOpen, setManageColumnsOpen] = useState(false);

  // Hooks
  const tablePrefs = useTablePreferences(entityType, columns, true);
  const viewsHook = useDataTableViews({ entityType, tenantId });

  // Track if initial view has been applied
  const initialViewAppliedRef = useRef(false);

  // Apply initial view when currentViewId is set on mount
  useEffect(() => {
    console.log('[useTablePage] Initial view check - currentViewId:', viewsHook.currentViewId, 'savedViews:', viewsHook.savedViews.length, 'applied:', initialViewAppliedRef.current);
    if (viewsHook.currentViewId && !initialViewAppliedRef.current && viewsHook.savedViews.length > 0) {
      const view = viewsHook.savedViews.find((v) => String(v.id) === viewsHook.currentViewId);
      console.log('[useTablePage] Found view:', view?.name, 'data:', view?.data);
      if (view && view.data) {
        console.log('[useTablePage] Applying initial view:', view.name);
        // Apply table preferences
        tablePrefs.applyViewPreferences({
          columnOrder: view.data.columnOrder || [],
          visibleColumns: view.data.visibleColumns || [],
          columnWidths: view.data.columnWidths || {},
        });

        // Apply sort
        if (view.data.sortBy !== undefined) {
          console.log('[useTablePage] Applying sort:', view.data.sortBy, view.data.sortOrder);
          setSortBy(view.data.sortBy);
        }
        if (view.data.sortOrder) {
          setSortOrder(view.data.sortOrder);
        }

        // Apply filters
        if (view.data.filters && onFiltersChange) {
          console.log('[useTablePage] Applying filters:', view.data.filters);
          onFiltersChange(view.data.filters);
        }

        initialViewAppliedRef.current = true;
        console.log('[useTablePage] Initial view applied successfully');
      }
    }
  }, [viewsHook.currentViewId, viewsHook.savedViews, tablePrefs, onFiltersChange]);

  // Computed: visible columns in order
  const visibleColumns = useMemo(() => {
    return tablePrefs.preferences.columnOrder.filter(key =>
      tablePrefs.preferences.visibleColumns.includes(key)
    );
  }, [tablePrefs.preferences.columnOrder, tablePrefs.preferences.visibleColumns]);

  // Computed: total width
  const totalWidth = useMemo(() => {
    return calculateTableWidth(visibleColumns, tablePrefs.getColumnWidth);
  }, [visibleColumns, tablePrefs.getColumnWidth]);

  // Computed: filtered + sorted data
  const sortedData = useMemo(() => {
    let filtered = data;

    // Apply search query (client-side only — skip when search is handled server-side)
    const query = searchQuery.toLowerCase();
    if (query && getSearchText) {
      filtered = filtered.filter(item => {
        const texts = getSearchText(item);
        return texts.some(text => text && text.toLowerCase().includes(query));
      });
    }

    // Apply structured filters
    if (filters && Object.keys(filters).length > 0) {
      filtered = filtered.filter(item => {
        return Object.entries(filters).every(([key, filterValue]) => {
          // Skip empty filters
          if (filterValue === undefined || filterValue === null || filterValue === '') return true;
          if (Array.isArray(filterValue) && filterValue.length === 0) return true;

          const itemValue = (item as any)[key];

          if (Array.isArray(filterValue)) {
            return filterValue.includes(itemValue);
          }

          return itemValue === filterValue;
        });
      });
    }

    // Sort
    if (!sortBy) return filtered;

    return [...filtered].sort((a, b) => {
      const aValue = getSortValue(a, sortBy);
      const bValue = getSortValue(b, sortBy);

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, searchQuery, sortBy, sortOrder, getSearchText, getSortValue, filters]);

  // Handlers
  const handleRowClick = useCallback((e: React.MouseEvent, item: T) => {
    setMenuPosition({ top: e.clientY, left: e.clientX });
    setSelectedItem(item);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuPosition(null);
    setSelectedItem(null);
  }, []);

  const handleStatusClick = useCallback((e: React.MouseEvent<HTMLElement>, item: T) => {
    setStatusAnchorEl(e.currentTarget);
    setSelectedItem(item);
  }, []);

  const handleStatusMenuClose = useCallback(() => {
    setStatusAnchorEl(null);
    setSelectedItem(null);
  }, []);

  const handleColumnHeaderClick = useCallback((columnKey: string) => {
    if (sortBy === columnKey) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnKey);
      setSortOrder('asc');
    }
  }, [sortBy]);

  const getSortIndicator = useCallback((columnKey: string): string | null => {
    return sortBy === columnKey ? (sortOrder === 'asc' ? '▲' : '▼') : null;
  }, [sortBy, sortOrder]);

  const handleColumnResize = useCallback((columnKey: string, newWidth: number) => {
    tablePrefs.updateColumnWidth(columnKey, newWidth);
  }, [tablePrefs]);

  const handleVisibleColumnsChange = useCallback((cols: string[]) => {
    tablePrefs.updateVisibleColumns(cols);
  }, [tablePrefs]);

  const handleColumnOrderChange = useCallback((order: string[]) => {
    tablePrefs.updateColumnOrder(order);
  }, [tablePrefs]);

  // Data loading helper
  const loadDataRef = useRef<number>(0);
  const loadData = useCallback(async (fetcher: () => Promise<T[]>, errorMessage?: string) => {
    const callId = ++loadDataRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await fetcher();
      if (callId === loadDataRef.current) {
        setData(result);
      }
    } catch (err) {
      if (callId === loadDataRef.current) {
        setError(err instanceof Error ? err.message : (errorMessage || 'Failed to load data'));
      }
    } finally {
      if (callId === loadDataRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // View handlers
  const handleSelectView = useCallback((viewId: string | number) => {
    const view = viewsHook.savedViews.find((v) => Number(v.id) === Number(viewId));

    if (view && view.data) {
      tablePrefs.applyViewPreferences({
        columnOrder: view.data.columnOrder || [],
        visibleColumns: view.data.visibleColumns || [],
        columnWidths: view.data.columnWidths || {},
      });

      setSortBy(view.data.sortBy || null);
      setSortOrder(view.data.sortOrder || 'asc');

      // Apply filters if they exist in the view data
      if (view.data.filters && onFiltersChange) {
        onFiltersChange(view.data.filters);
      }

      viewsHook.setCurrentViewId(String(viewId));
      listViewService.saveLatestChosenView(String(tenantId), String(viewId));
    } else {
      toast.error('View not found');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tablePrefs, tenantId, viewsHook, onFiltersChange]);

  const handleSaveView = useCallback(async () => {
    if (!viewsHook.currentViewId || !tenantId) return;

    try {
      const viewData: listViewService.ListViewData = {
        columnOrder: tablePrefs.preferences.columnOrder,
        visibleColumns: tablePrefs.preferences.visibleColumns,
        columnWidths: tablePrefs.preferences.columnWidths,
        sortBy,
        sortOrder,
        filters,
      };

      await listViewService.updateListView(viewsHook.currentViewId, tenantId, {
        data: viewData,
      });

      await viewsHook.refreshViews();
      toast.success('View saved');
    } catch (err) {
      console.error('Error saving view:', err);
      toast.error('Failed to save view');
    }
  }, [viewsHook, tenantId, tablePrefs.preferences, sortBy, sortOrder, filters]);

  const handleSaveAsView = useCallback(() => {
    viewsHook.setSaveViewModalOpen(true);
  }, [viewsHook]);

  const handleSaveViewModalSave = useCallback(async (viewName: string, isPublic: boolean) => {
    if (!tenantId) return;

    try {
      const viewData: listViewService.ListViewData = {
        columnOrder: tablePrefs.preferences.columnOrder,
        visibleColumns: tablePrefs.preferences.visibleColumns,
        columnWidths: tablePrefs.preferences.columnWidths,
        sortBy,
        sortOrder,
        filters,
      };

      const newView = await listViewService.saveListView(entityType, tenantId, {
        name: viewName,
        type: isPublic ? 'public' : 'private',
        data: viewData,
      });

      await viewsHook.refreshViews();
      viewsHook.setCurrentViewId(String(newView.id));
      listViewService.saveLatestChosenView(String(tenantId), String(newView.id));

      toast.success(`View "${viewName}" saved`);
      viewsHook.setSaveViewModalOpen(false);
    } catch (err) {
      console.error('Error saving new view:', err);
      toast.error('Failed to save view');
    }
  }, [tenantId, entityType, tablePrefs.preferences, sortBy, sortOrder, filters, viewsHook]);

  // Pre-built prop objects
  const toolbarProps = useMemo(() => ({
    count: data.length,
    filteredCount: sortedData.length,
    searchQuery,
    onSearchChange: setSearchQuery,
    views: viewsHook.savedViews,
    currentViewId: viewsHook.currentViewId,
    onSelectView: handleSelectView,
    onSaveView: handleSaveView,
    onSaveAsView: handleSaveAsView,
    onDeleteView: viewsHook.handleDeleteView,
    onSetDefaultView: viewsHook.handleSetDefaultView,
    onManageColumns: () => setManageColumnsOpen(true),
  }), [data.length, sortedData.length, searchQuery, viewsHook, handleSelectView, handleSaveView, handleSaveAsView]);

  const headerProps = useMemo(() => ({
    columns: tablePrefs.columnConfigs,
    visibleColumns,
    getColumnWidth: tablePrefs.getColumnWidth,
    onColumnResize: handleColumnResize,
    onSort: handleColumnHeaderClick,
    getSortIndicator,
  }), [tablePrefs.columnConfigs, visibleColumns, tablePrefs.getColumnWidth, handleColumnResize, handleColumnHeaderClick, getSortIndicator]);

  const containerProps = useMemo(() => ({
    totalWidth,
  }), [totalWidth]);

  const menuProps = useMemo(() => ({
    position: menuPosition,
    open: Boolean(menuPosition),
    onClose: handleMenuClose,
  }), [menuPosition, handleMenuClose]);

  const manageColumnsProps = useMemo(() => ({
    open: manageColumnsOpen,
    columns: tablePrefs.columnConfigs,
    visibleColumns: tablePrefs.preferences.visibleColumns,
    columnOrder: tablePrefs.preferences.columnOrder,
    onColumnsChange: handleVisibleColumnsChange,
    onOrderChange: handleColumnOrderChange,
    onResetDefaults: tablePrefs.resetPreferences,
    onClose: () => setManageColumnsOpen(false),
  }), [manageColumnsOpen, tablePrefs, handleVisibleColumnsChange, handleColumnOrderChange]);

  const saveViewModalProps = useMemo(() => ({
    open: viewsHook.saveViewModalOpen,
    onSave: handleSaveViewModalSave,
    onClose: () => viewsHook.setSaveViewModalOpen(false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [viewsHook.saveViewModalOpen, handleSaveViewModalSave]);

  const tableBodyProps = useMemo(() => ({
    visibleColumns,
    columns: tablePrefs.columnConfigs,
    getColumnWidth: tablePrefs.getColumnWidth,
    onColumnResize: handleColumnResize,
    onSort: handleColumnHeaderClick,
    getSortIndicator,
    totalWidth,
  }), [visibleColumns, tablePrefs.columnConfigs, tablePrefs.getColumnWidth, handleColumnResize, handleColumnHeaderClick, getSortIndicator, totalWidth]);

  return {
    data,
    setData,
    sortedData,
    loading,
    setLoading,
    error,
    setError,
    loadData,
    searchQuery,
    setSearchQuery,
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder,
    menuPosition,
    selectedItem,
    handleRowClick,
    handleMenuClose,
    statusAnchorEl,
    handleStatusClick,
    handleStatusMenuClose,
    visibleColumns,
    totalWidth,
    tablePrefs,
    manageColumnsOpen,
    setManageColumnsOpen,
    viewsHook,
    toolbarProps,
    headerProps,
    containerProps,
    menuProps,
    manageColumnsProps,
    saveViewModalProps,
    tableBodyProps,
  };
}

export default useTablePage;
